import { Stack } from "@mantine/core";
import { useReactFlow } from "@xyflow/react";
import { DateTime } from "luxon";
import { useContext, useState } from "react";
import { useSitesDetail } from "../../../../hooks/useSitesDetail";
import { useSiteWizardMutations } from "../../../../hooks/useSiteWizardMutations";
import { useToast } from "../../../../mantine/notifications/useToast";
import urls from "../../../../urls";
import { pollTaskStatus } from "../../../../utils/api-utils";
import type { SiteWizard } from "../../../../utils/backend-types";
import { StructureViewModeContext } from "../../../StructureView/StructureViewModeContext";
import { createMeteringConceptPdf } from "../../../StructureView/utils/createMeteringConceptPdf";
import { OnboardingTodo } from "../../../Todos/Todos.constants";
import { getOnboardingTodoLabel } from "../../../Todos/utils/getOnboardingTodoLabel";
import { OnboardingPhase } from "../OnboardingPhase/OnboardingPhase";
import {
  AssignmentType,
  ONBOARDING_PHASES
} from "../OnboardingWizard.constants";
import { ConfigurationAssignment } from "./ConfigurationAssignment/ConfigurationAssignment";
import { ContractsAssignment } from "./ContractsAssignment/ContractsAssignment";
import { InvoiceAssignment } from "./InvoiceAssignment/InvoiceAssignment";
import { MeteringConceptAssignment } from "./MeteringConceptAssignment/MeteringConceptAssignment";
import { OptiNodeAssignment } from "./OptiNodeAssignment/OptiNodeAssignment";
import { RegulatoryDataAssignment } from "./RegulatoryDataAssignment/RegulatoryDataAssignment";
import { UserDocuments } from "./VollmachtAssignment/UserDocuments/UserDocuments";
import { VollmachtAssignment } from "./VollmachtAssignment/VollmachtAssignment";

export interface OnboardingAssignmentProps {
  siteWizard: SiteWizard;
  type: AssignmentType;
  projectId: string;
  onSiteWizardUpdated: () => Promise<void>;
}

function OnboardingAssignment({
  siteWizard,
  type,
  projectId,
  onSiteWizardUpdated
}: OnboardingAssignmentProps) {
  const isDone = ONBOARDING_PHASES.get(type)?.done(siteWizard) ?? false;
  const displayPhase =
    ONBOARDING_PHASES.get(type)?.displayPhase(siteWizard) ?? false;

  const [assignmentIsLoading, setAssignmentIsLoading] =
    useState<AssignmentType | null>(null);
  const { structureViewMode, updateStructureViewMode } = useContext(
    StructureViewModeContext
  );
  const { updateOnboardingPhaseDoneMutation } = useSiteWizardMutations(
    siteWizard.id
  );
  const updateOnboardingPhaseDone =
    updateOnboardingPhaseDoneMutation.mutateAsync;
  const { getNodes } = useReactFlow();
  const { showToast, showErrorToast } = useToast();

  const { data: siteDetails } = useSitesDetail(siteWizard.id);

  async function updateSiteWizard(type: AssignmentType) {
    setAssignmentIsLoading(type);
    const currentYear = DateTime.now().year;
    let phase: string = "";

    switch (type) {
      case AssignmentType.Configuration:
        phase = getOnboardingTodoLabel(
          OnboardingTodo.KONFIGURATION_PRUEFEN,
          currentYear
        );
        break;
      case AssignmentType.MeteringConcept:
        createMeteringConceptPdf(
          showErrorToast,
          siteDetails,
          siteWizard.id,
          structureViewMode,
          updateStructureViewMode,
          getNodes
        );
        phase = getOnboardingTodoLabel(
          OnboardingTodo.MESSKONZEPT_UND_ZAEHLERKONFIGURATION_DATEN_VERVOLLSTAENDIGEN,
          currentYear
        );
        break;
      case AssignmentType.Regulatory:
        phase = getOnboardingTodoLabel(
          OnboardingTodo.REGULATORISCHE_DATEN_VERVOLLSTAENDIGEN,
          currentYear
        );
        break;
      case AssignmentType.VollmachtMsb:
        phase = getOnboardingTodoLabel(
          OnboardingTodo.VOLLMACHT_FUER_MSB_BEREITSTELLEN,
          currentYear
        );
        break;
      case AssignmentType.VollmachtVermarkter:
        phase = getOnboardingTodoLabel(
          OnboardingTodo.VOLLMACHT_FUER_VERMARKTER_BEREITSTELLEN,
          currentYear
        );
        break;
      case AssignmentType.Invoices:
        phase = getOnboardingTodoLabel(
          OnboardingTodo.LIEFERKONDITIONEN_HINTERLEGEN_STROMLIEFERUNG_UND_ABRECHNUNG,
          currentYear
        );
        break;
      case AssignmentType.VermarktungContracts:
        phase = getOnboardingTodoLabel(
          OnboardingTodo.DIREKTVERMARKTUNGSVERTRAEGE_HINTERLEGEN,
          currentYear
        );
        break;
      case AssignmentType.P6Contracts:
        phase = getOnboardingTodoLabel(
          OnboardingTodo.P6_VERTRAEGE_HINTERLEGEN,
          currentYear
        );
        break;
      default:
        console.error(`Assignment type: ${type} not implemented`);
        break;
    }

    await updateOnboardingPhaseDone({ onboardingPhase: phase, isDone: true })
      .catch(() => {
        showErrorToast("Fehler beim aktualisieren der Daten.");
      })
      .then((res) => {
        if (type === AssignmentType.MeteringConcept && res?.data) {
          const taskId: string = res.data.taskIds[0];
          const onError = () => {
            showErrorToast("Fehler beim Erstellen der Messkonzept-Dokumente.");
            setAssignmentIsLoading(null);
          };
          pollTaskStatus(
            urls.api.jobStatus(taskId),

            //onSuccess
            () => {
              showToast({
                type: "success",
                message: "Messkonzept Dokumente erstellt."
              });
              setAssignmentIsLoading(null);
            },

            //onError
            onError
          );
        }
        onSiteWizardUpdated();
      })
      .finally(() => {
        if (type !== AssignmentType.MeteringConcept) {
          setAssignmentIsLoading(null);
        }
      });
  }

  function getAssignmentComponent() {
    switch (type) {
      case AssignmentType.MeteringConcept:
        return (
          <MeteringConceptAssignment
            done={siteWizard.phases.meteringConcept.done}
            isDisabled={!siteWizard.phases.configuration.done}
            isLoading={assignmentIsLoading === AssignmentType.MeteringConcept}
            missingData={siteWizard.phases.meteringConcept.missingData}
            projectId={projectId}
            siteId={siteWizard.id}
            updateSiteWizard={updateSiteWizard}
          />
        );
      case AssignmentType.Configuration:
        return (
          <ConfigurationAssignment
            done={siteWizard.phases.configuration.done}
            missingData={siteWizard.phases.configuration.missingData}
            projectId={projectId}
            siteId={siteWizard.id}
            updateSiteWizard={updateSiteWizard}
          />
        );
      case AssignmentType.Regulatory:
        return (
          <RegulatoryDataAssignment
            done={siteWizard.phases.regulatory.done}
            isDisabled={!siteWizard.phases.configuration.done}
            missingData={siteWizard.phases.regulatory.missingData}
            updateSiteWizard={updateSiteWizard}
          />
        );
      case AssignmentType.Vollmachten: {
        const msbauthorityVisible = siteWizard.phases.msbauthority.displayPhase;
        const dataloadauthorityVisible =
          siteWizard.phases.dataloadauthority.displayPhase;
        const allVisibleVollmachtenDone =
          (!msbauthorityVisible || siteWizard.phases.msbauthority.done) &&
          (!dataloadauthorityVisible ||
            siteWizard.phases.dataloadauthority.done);

        return (
          <>
            {!allVisibleVollmachtenDone && (
              <VollmachtAssignment
                siteWizard={siteWizard}
                updateSiteWizard={updateSiteWizard}
              />
            )}
            {(siteWizard.phases.msbauthority.displayPhase ||
              siteWizard.phases.dataloadauthority.displayPhase) && (
              <UserDocuments siteId={siteWizard.id} />
            )}
          </>
        );
      }
      case AssignmentType.Contracts: {
        const vermarktungContractsVisible =
          siteWizard.phases.vermarktungContracts.displayPhase;
        const p6ContractsVisible = siteWizard.phases.p6Contracts.displayPhase;
        const allVisibleContractsDone =
          (!vermarktungContractsVisible ||
            siteWizard.phases.vermarktungContracts.done) &&
          (!p6ContractsVisible || siteWizard.phases.p6Contracts.done);

        if (allVisibleContractsDone) {
          return null;
        }

        return (
          <Stack gap={3} mt={10}>
            {vermarktungContractsVisible && (
              <ContractsAssignment
                contractType="dv"
                done={siteWizard.phases.vermarktungContracts.done}
                isDisabled={!siteWizard.phases.configuration.done}
                objectsAreMissing={
                  siteWizard.phases.vermarktungContracts.objectsAreMissing
                }
                updateSiteWizard={() =>
                  updateSiteWizard(AssignmentType.VermarktungContracts)
                }
              />
            )}
            {p6ContractsVisible && (
              <ContractsAssignment
                contractType="p6"
                done={siteWizard.phases.p6Contracts.done}
                isDisabled={!siteWizard.phases.configuration.done}
                objectsAreMissing={
                  siteWizard.phases.p6Contracts.objectsAreMissing
                }
                updateSiteWizard={() =>
                  updateSiteWizard(AssignmentType.P6Contracts)
                }
              />
            )}
          </Stack>
        );
      }
      case AssignmentType.Invoices:
        return (
          <InvoiceAssignment
            done={siteWizard.phases.invoices.done}
            isDisabled={!siteWizard.phases.configuration.done}
            missingData={siteWizard.phases.invoices.missingData}
            objectsAreMissing={siteWizard.phases.invoices.objectsAreMissing}
            projectId={projectId}
            updateSiteWizard={updateSiteWizard}
          />
        );
      case AssignmentType.OptiNode:
        return (
          <OptiNodeAssignment
            done={siteWizard.onboardingDone.onboardingDone}
            mbkTodoDone={siteWizard.onboardingDone.todoStatus.mbkTodo}
            msbDataTodoDone={siteWizard.onboardingDone.todoStatus.msbDataTodo}
            regulatoryTodoDone={
              siteWizard.onboardingDone.todoStatus.regulatoryTodo
            }
            siteId={siteWizard.id}
            onSiteWizardUpdated={onSiteWizardUpdated}
          />
        );
      default:
        console.error(`Assignment type: ${type} not implemented`);
        return null;
    }
  }

  if (!displayPhase) return null;

  return (
    <OnboardingPhase isDone={isDone} type={type}>
      {getAssignmentComponent()}
    </OnboardingPhase>
  );
}

export { OnboardingAssignment };
